import t from '@rcm/i18n-client';
import { InvisibleReCaptcha2Button, useCaptchaActionEnabled } from '@rcm/recaptcha-2';
import { CONTACT } from '@reliv/pws-contact';
import axios from 'axios';
import classNames from 'classnames';
import type { BlockComponentProps as Props } from 'client-react-block-renderer';
import type { RestResponse } from 'forms';
import { Form, getFieldError, InputTextField, TextAreaField } from 'forms';
import type { FormEvent } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import './PwsContactFormBlock.scss';

const API_URL = '/api/pws-contact';

const ContactFormBlock = (props: Props) => {
  const [captcha, setCaptcha] = useState('');
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<{ message?: string; fields?: any }>();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [success, setSuccess] = useState(false);
  const [waiting, setWaiting] = useState(false);

  const isCaptchaEnabled = useCaptchaActionEnabled(CONTACT);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      if (waiting) {
        return;
      }

      event.preventDefault();
      setWaiting(true);
      try {
        await axios.post(API_URL, {
          name,
          comment,
          email,
          phone,
          ...(isCaptchaEnabled ? { captcha } : {}),
        });
        setSuccess(true);
        setError(null);
      } catch (responseError) {
        const response: RestResponse = responseError.response;
        setError(response.data.error);
      } finally {
        setWaiting(false);
        window.scrollTo(0, 0);
      }
    },
    [captcha, comment, email, isCaptchaEnabled, name, phone, waiting]
  );

  const err = getFieldError(error && error.fields);

  useEffect(() => {
    if (error && isCaptchaEnabled) {
      window?.grecaptcha?.reset();
    }
  }, [error, isCaptchaEnabled]);

  return (
    <div className={classNames('pws-contact-form', { 'pws-contact-form-success': success })}>
      {!!(error && error.message) && <p className="alert alert-warning">{error.message}</p>}
      {success ? (
        <p>{t('Thank you! Your request has been submitted.')}</p>
      ) : (
        <div className="row">
          <div className="col-sm-7 col-xs-12">
            <Form onSubmit={handleSubmit} className={waiting ? 'processing' : ''}>
              <div>
                <InputTextField
                  error={err('name')}
                  label={t('Name')}
                  name="name"
                  onValueChange={setName}
                  tabIndex={1}
                  value={name}
                />
                <InputTextField
                  error={err('email')}
                  label={t('Email')}
                  name="email"
                  onValueChange={setEmail}
                  tabIndex={1}
                  value={email}
                />
                <InputTextField
                  error={err('phone')}
                  label={t('Phone')}
                  name="phone"
                  onValueChange={setPhone}
                  tabIndex={1}
                  value={phone}
                />
                <TextAreaField
                  className="commentField"
                  label={t('Comment')}
                  name="comment"
                  onValueChange={setComment}
                  tabIndex={1}
                  value={comment}
                />
              </div>
              <div className="pws-contact-form-submit-container">
                <InvisibleReCaptcha2Button
                  action={CONTACT}
                  badge="inline"
                  btn="primary"
                  disabled={waiting}
                  name="captcha"
                  onValueChange={setCaptcha}
                  tabIndex={1}
                  text={t('Submit')}
                  type="submit"
                />
              </div>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactFormBlock;
